<template>
  <div
    :class="[
      'relative rounded-md m-2 shadow p-3 border-2 bg-neutral-50 flex flex-col justify-between h-40 transition-all',
      clickable ? 'cursor-pointer hover:shadow-lg hover:border-accent-primary hover:-translate-y-0.5' : '',
      borderClass,
      fakeData ? 'border-dashed' : '',
    ]"
    @click="handleClick"
  >
    <div
      v-if="fakeData"
      class="absolute -top-2 right-2 bg-amber-400 text-amber-900 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full shadow-sm font-poppins pointer-events-none"
      title="Sample data — not yet wired to live sources"
    >
      sample
    </div>
    <div class="flex flex-row items-start justify-between">
      <div class="text-xs tracking-widest text-gray-600 uppercase font-poppins">
        {{ title }}
      </div>
      <div
        v-if="badge"
        :class="['px-2 py-0.5 rounded-full text-xs font-poppins whitespace-nowrap', badgeClass]"
      >
        {{ badge }}
      </div>
    </div>

    <div class="flex flex-col items-start">
      <div class="flex flex-row items-baseline space-x-2">
        <div :class="['text-3xl font-semibold font-poppins', valueClass]">
          {{ formattedValue }}
        </div>
        <div v-if="denominator !== null && denominator !== undefined" class="text-base text-gray-500">
          / {{ formatNumbersWithCommas(denominator) }}
        </div>
      </div>
      <div v-if="subtitle" class="text-xs text-gray-500 mt-1">
        {{ subtitle }}
      </div>
    </div>

    <div class="flex flex-row items-center justify-between text-xs text-gray-500">
      <div v-if="trend" :class="trendClass">
        {{ trendArrow }} {{ trend }}
      </div>
      <div v-else></div>
      <div v-if="clickable" class="text-accent-primary font-poppins">Details &rsaquo;</div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import { useRouter } from 'vue-router'
import { formatNumbersWithCommas } from '@/composables/FormatNumbers'

const props = defineProps({
  title: { type: String, required: true },
  value: { type: [Number, String], required: true },
  denominator: { type: [Number, String], default: null },
  subtitle: { type: String, default: '' },
  badge: { type: String, default: '' },
  badgeTone: { type: String, default: 'neutral' },
  valueTone: { type: String, default: 'default' },
  borderTone: { type: String, default: 'default' },
  trend: { type: String, default: '' },
  trendDirection: { type: String, default: 'flat' },
  to: { type: String, default: '' },
  fakeData: { type: Boolean, default: false },
})

const router = useRouter()

const clickable = computed(() => Boolean(props.to))

const formattedValue = computed(() => {
  if (typeof props.value === 'number') return formatNumbersWithCommas(props.value)
  return props.value
})

const badgeClass = computed(() => {
  switch (props.badgeTone) {
    case 'good':   return 'bg-emerald-100 text-emerald-700'
    case 'warn':   return 'bg-amber-100 text-amber-800'
    case 'bad':    return 'bg-rose-100 text-rose-700'
    case 'info':   return 'bg-sky-100 text-sky-800'
    default:       return 'bg-gray-200 text-gray-700'
  }
})

const valueClass = computed(() => {
  switch (props.valueTone) {
    case 'good': return 'text-emerald-700'
    case 'warn': return 'text-amber-700'
    case 'bad':  return 'text-rose-700'
    default:     return 'text-dark-primary'
  }
})

const borderClass = computed(() => {
  switch (props.borderTone) {
    case 'good': return 'border-emerald-300'
    case 'warn': return 'border-amber-300'
    case 'bad':  return 'border-rose-300'
    case 'info': return 'border-sky-300'
    default:     return 'border-gray-300'
  }
})

const trendArrow = computed(() => {
  if (props.trendDirection === 'up')   return '\u2191'
  if (props.trendDirection === 'down') return '\u2193'
  return '\u2192'
})

const trendClass = computed(() => {
  if (props.trendDirection === 'up')   return 'text-emerald-600'
  if (props.trendDirection === 'down') return 'text-rose-600'
  return 'text-gray-500'
})

function handleClick() {
  if (props.to) router.push(props.to)
}
</script>
